import type { Pill } from '../../api/types.js';
import { accentFor, color, font, fontSize, radius, tint } from '../../theme/tokens.js';
import { Disclosure } from '../Disclosure/Disclosure.js';
import { EM_DASH } from '../MetricCell/MetricCell.js';

/**
 * Region pills (FR-030, PRD §6.2).
 *
 * **Always all six, in a fixed order.** Pills are a navigation set, not a query result: a
 * region with no recruiters still renders its pill showing `0`. If a region could vanish
 * because it had no rows this week, the way into it would vanish with it.
 *
 * ## The pills scope the chart; they do not navigate (owner decision, 2026-08-28)
 *
 * They replaced the Overview's `Chart region` segmented control, which listed the same
 * codes a few pixels to their right — two controls for one scope, and the more prominent of the
 * two did something else entirely. **Region Detail is reached from the table row or the card**,
 * which is the click that was already carrying that meaning everywhere else in the product.
 *
 * ## They select many, and each one deselects itself (owner decision, 2026-08-28)
 *
 * `selected` is a **list**: several regions chart as their sum, and clicking a selected pill
 * takes it back out. That is why they are toggles rather than a segmented control — a segmented
 * control cannot express two of six, and it cannot express "unpick this one" at all.
 *
 * **An empty list is `ALL`, not an empty chart.** Deselecting the last region therefore lands on
 * the page's default scope rather than on a blank card, and there is no state in which the chart
 * has nothing to draw.
 *
 * `null` from `onSelect` is that All scope, not a region: the page's `ALL` sentinel is a chart
 * scope the timeseries route accepts and nothing else does, so it deliberately stops at the page
 * boundary rather than entering a component that renders `UiRegion`s.
 *
 * ## The chip shows the code and the recruiter count, but not the name (2026-08-28)
 *
 * The same departure `<RegionStrip>` records for the Specialties page, for the same reason and
 * with the same remedy: the name is the longest and least informative part of the face — the
 * codes are how a region is named everywhere else — and dropping it keeps the six on one line.
 *
 * The name still reaches the reader through `<Disclosure>` rather than a `title` attribute, so
 * it appears on **keyboard focus as well as hover** (FR-062), and `pressed` is what tells a
 * screen-reader user that the accent ring means "the chart is showing this one".
 */

export interface RegionPillsProps {
  pills: Pill[];
  /**
   * A region code toggles that region in or out of the selection; `null` clears back to **All
   * regions**. The page owns the set — this component only reports the click.
   */
  onSelect: (regionCode: string | null) => void;
  /** The regions currently charted. Empty means all six combined. */
  selected?: readonly string[];
  loading?: boolean;
}

/** Neutral by design: **All** is the absence of a region, so it borrows no region's accent. */
/* `inkMuted` as a fill, not as ink — see the identical note in `<RegionStrip>`. Both strips must
 * show the ALL chip the same way; they sit one click apart. */
const ALL_ACCENT = color.inkMuted;

export function RegionPills({ pills, onSelect, selected = [], loading }: RegionPillsProps) {
  if (loading) {
    return (
      <div style={{ display: 'flex', gap: 10 }}>
        {Array.from({ length: 8 }, (_unused, i) => (
          <div key={i} className="skeleton" style={{ height: 30, width: 82 }} aria-hidden="true" />
        ))}
      </div>
    );
  }

  return (
    <div
      role="group"
      style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}
      aria-label="Chart regions"
    >
      <span
        style={{
          fontSize: fontSize.caption,
          fontWeight: 600,
          letterSpacing: '.5px',
          color: color.inkSubtle,
          textTransform: 'uppercase',
          marginRight: 2,
        }}
      >
        Regions
      </span>

      {/*
       * No count on the face and none in the panel. `SUM(pills)` is deliberately *smaller* than
       * the Recruiters KPI directly below it, which adds manager seats on top of recruiters
       * (PRD §9.7.2, §11.3) — a total here would sit inches from a larger one measuring
       * something else, and read as one of the two being broken.
       */}
      <Chip
        code="ALL"
        accent={ALL_ACCENT}
        accessibleName="All regions combined"
        panelTitle="All regions"
        panelDetail="The six combined"
        active={selected.length === 0}
        onClick={() => onSelect(null)}
      />

      {pills.map((pill) => {
        const count = pill.recruiterCount;

        return (
          <Chip
            key={pill.regionCode}
            code={pill.regionCode}
            accent={accentFor(pill.regionCode)}
            // The name leads: it is what the code stands for and no longer sits on the face.
            accessibleName={`${pill.regionName} (${pill.regionCode}), ${
              count ?? 'no'
            } recruiters`}
            panelTitle={`${pill.regionCode} · ${pill.regionName}`}
            panelDetail={`Recruiters: ${count ?? EM_DASH}`}
            face={count ?? EM_DASH}
            active={selected.includes(pill.regionCode)}
            // A toggle both ways: this adds the region to the chart, and clicking it again
            // takes it back out — down to none, which is all six.
            onClick={() => onSelect(pill.regionCode)}
          />
        );
      })}
    </div>
  );
}

function Chip({
  code,
  accent,
  accessibleName,
  panelTitle,
  panelDetail,
  face,
  active,
  onClick,
}: {
  code: string;
  accent: string;
  accessibleName: string;
  panelTitle: string;
  panelDetail: string;
  face?: number | string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Disclosure
      accessibleName={accessibleName}
      panel={
        <span style={{ display: 'block', textAlign: 'left', whiteSpace: 'nowrap' }}>
          <span style={{ display: 'block', fontWeight: 700 }}>{panelTitle}</span>
          <span style={{ display: 'block', fontFamily: font.mono, marginTop: 3 }}>
            {panelDetail}
          </span>
        </span>
      }
      onClick={onClick}
      pressed={active}
      className="chip"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        background: active ? tint(accent, 8) : color.surface,
        border: `1px solid ${active ? accent : color.borderStrong}`,
        borderRadius: radius.pill,
        // The blueprint's own `6px 13px 6px 10px` — the extra right padding pays for the count
        // sitting to the badge's right.
        padding: face == null ? '6px 10px' : '6px 13px 6px 10px',
        cursor: 'pointer',
        fontSize: fontSize.bodyStrong,
        fontWeight: 500,
        color: color.inkRow,
        fontFamily: 'inherit',
      }}
    >
      <span
        className="mono"
        style={{
          fontFamily: font.mono,
          fontSize: '10px',
          fontWeight: 600,
          color: color.onAccent,
          background: accent,
          padding: '1px 6px',
          borderRadius: radius.sm,
        }}
      >
        {code}
      </span>
      {face == null ? null : (
        <span
          className="mono"
          style={{ fontFamily: font.mono, color: color.inkFaint, fontSize: fontSize.caption }}
        >
          {face}
        </span>
      )}
    </Disclosure>
  );
}
